import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  TextField,
  Button,
  FormControl,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Autocomplete,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const apiUrl = process.env.REACT_APP_API_URL;

const EditFarrierRecord = () => {
  const { t } = useTranslation();
  const { record_id } = useParams();
  const navigate = useNavigate();
  const [proceduresList, setProceduresList] = useState([]);
  const [medicationsList, setMedicationsList] = useState([]);
  const [formData, setFormData] = useState({
    record_date: '',
    work_place: 'Inside',
    horse_name: '',
    rfid_chip_number: '',
    owner_name: '',
    owner_phone: '',
    total_amount: 0,
    payment_type_id: null,
    notes: '',
    procedures: [],
    medications: [],
  });

  const fetchFarrierRecord = useCallback(async () => {
    try {
      const response = await api.get(`${apiUrl}/farrier-records/${record_id}`);
      const recordData = response.data;

      if (typeof recordData.file_uploads === 'string') {
        recordData.file_uploads = recordData.file_uploads
          ? recordData.file_uploads.split(';')
          : [];
      }

      // Owner fields (display only)
      recordData.owner_name = recordData.OwnerName || recordData.owner_name || '';
      recordData.owner_phone = recordData.phone || recordData.owner_phone || '';

      if (recordData.procedures) {
        recordData.procedures = recordData.procedures.split(';').map((proc) => {
          const [procedure_name, price] = proc.split('::');
          const match = proceduresList.find(
            (p) =>
              p.procedure_name.trim().toLowerCase() ===
              procedure_name.trim().toLowerCase()
          );

          return {
            procedure_id: match?.procedure_id || '',
            procedure_name: match?.procedure_name || procedure_name,
            price: parseFloat(price) || 0,
          };
        });
      }

      // Materials arrive as a JSON array (JSON_ARRAYAGG) — robust against names with ';'/'::'.
      let rawMaterials = recordData.medications;
      if (typeof rawMaterials === 'string') {
        try {
          rawMaterials = JSON.parse(rawMaterials);
        } catch {
          rawMaterials = [];
        }
      }
      recordData.medications = Array.isArray(rawMaterials)
        ? rawMaterials.map((med) => {
            const idRaw = med.medication_id;
            const match = medicationsList.find(
              (m) => m.medication_id.toString() === String(idRaw)
            );
            return {
              medication_id: match?.medication_id || parseInt(idRaw, 10),
              quantity: med.quantity,
              // Unit price (line total is recomputed at submit, matching the Add page)
              price: Number(match?.price) || 0,
            };
          })
        : [];

      setFormData(recordData);
    } catch (error) {
      console.error('Error fetching farrier record', error);
    }
  }, [record_id, medicationsList, proceduresList]);

  const fetchProceduresList = useCallback(async () => {
    try {
      const response = await api.get(`${apiUrl}/farrier_procedures`);
      setProceduresList(response.data);
    } catch (error) {
      console.error('Error fetching procedures list:', error);
    }
  }, []);

  const fetchMedicationsList = useCallback(async () => {
    try {
      const response = await api.get(`${apiUrl}/farrier_materials`);
      setMedicationsList(response.data);
    } catch (error) {
      console.error('Error fetching medications list:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await fetchProceduresList();
      await fetchMedicationsList();
    };
    loadData();
  }, [fetchProceduresList, fetchMedicationsList]);

  useEffect(() => {
    if (proceduresList.length > 0 && medicationsList.length > 0) {
      fetchFarrierRecord();
    }
  }, [proceduresList, medicationsList, fetchFarrierRecord]);

  const handleProcedureChange = (procedure) => {
    setFormData((prevData) => {
      const exists = prevData.procedures?.some(
        (p) => p.procedure_id === procedure.procedure_id
      );
      const updatedProcedures = exists
        ? prevData.procedures.filter(
            (p) => p.procedure_id !== procedure.procedure_id
          )
        : [
            ...(prevData.procedures || []),
            { procedure_id: procedure.procedure_id, price: procedure.price || 0 },
          ];
      return { ...prevData, procedures: updatedProcedures };
    });
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData((prevData) => {
      const updatedMedications = prevData.medications.map((med, i) => {
        if (i !== index) return med;
        if (field === 'medication_id') {
          const selectedMed = medicationsList.find(
            (m) => m.medication_id === parseInt(value, 10)
          );
          if (selectedMed) {
            return {
              ...med,
              medication_id: selectedMed.medication_id,
              quantity: selectedMed.default_quantity || 1,
              price: Number(selectedMed.price) || 0,
            };
          }
          return { ...med, medication_id: '' };
        }
        return { ...med, [field]: value };
      });
      return { ...prevData, medications: updatedMedications };
    });
  };

  const addMedication = () => {
    setFormData((prevData) => ({
      ...prevData,
      medications: [
        ...(Array.isArray(prevData.medications) ? prevData.medications : []),
        { medication_id: '', quantity: '', price: 0 },
      ],
    }));
  };

  const removeMedication = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      medications: prevData.medications.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanedData = {
        ...formData,
        payment_type_id: formData.payment_type_id || null,
        procedures: Array.isArray(formData.procedures)
          ? formData.procedures.map((p) => ({
              ...p,
              price: parseFloat(p.price) || 0,
            }))
          : [],
        medications: Array.isArray(formData.medications)
          ? formData.medications
              .filter((m) => m.medication_id)
              .map((m) => ({
                medication_id: m.medication_id,
                quantity: m.quantity,
                dosage: null,
                unit: null,
                // Store the line total (unit price × quantity), matching the Add page
                price: (Number(m.price) || 0) * (Number(m.quantity) || 0),
              }))
          : [],
      };

      const proceduresTotal = cleanedData.procedures.reduce(
        (sum, procedure) => sum + (Number(procedure.price) || 0),
        0
      );
      const materialsTotal = cleanedData.medications.reduce(
        (sum, material) => sum + (Number(material.price) || 0),
        0
      );
      cleanedData.total_amount = proceduresTotal + materialsTotal;

      await api.put(`${apiUrl}/farrier-records/${record_id}`, cleanedData);

      alert('Record updated successfully!');
      navigate('/FarrierList');
    } catch (error) {
      console.error('Error updating record', error);
      alert(error?.response?.data?.error || 'Failed to update record. Please try again.');
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom textAlign="center">
        {t('Edit_Farrier_Record')}
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          label={t('horse_name')}
          value={formData.horse_name || ''}
          fullWidth
          margin="normal"
          disabled
        />
        <TextField
          label={t('rfid_chip_number')}
          value={formData.rfid_chip_number || ''}
          fullWidth
          margin="normal"
          disabled
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('owner_name')}
              value={formData.owner_name || ''}
              fullWidth
              margin="normal"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('phone')}
              value={formData.owner_phone || ''}
              fullWidth
              margin="normal"
              disabled
            />
          </Grid>
        </Grid>

        <FormControl component="fieldset" margin="normal">
          <Typography>Procedures</Typography>
          <Grid container spacing={2}>
            {proceduresList.map((procedure) => (
              <Grid item xs={12} sm={6} md={4} key={procedure.procedure_id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={procedure.procedure_id}
                      checked={formData.procedures?.some(
                        (p) => p.procedure_id === procedure.procedure_id
                      )}
                      onChange={() => handleProcedureChange(procedure)}
                    />
                  }
                  label={procedure.procedure_name}
                />
              </Grid>
            ))}
          </Grid>
        </FormControl>

        <FormControl component="fieldset" margin="normal" sx={{ width: '100%', gap: 1.5 }}>
          <Typography>{t('farrierMaterials.title')}</Typography>
          {formData.medications.map((medication, index) => (
            <Box key={index} sx={{ mb: 1.5 }}>
              <Autocomplete
                options={medicationsList}
                value={
                  medicationsList.find(
                    (med) =>
                      String(med.medication_id) === String(medication.medication_id)
                  ) || null
                }
                onChange={(_, selectedMaterial) =>
                  handleMedicationChange(
                    index,
                    'medication_id',
                    selectedMaterial?.medication_id || ''
                  )
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.medication_id) === String(value.medication_id)
                }
                getOptionLabel={(option) =>
                  option?.medication_name
                    ? `${option.medication_name} - ${t('farrierMaterials.stock')}: ${option.stock_quantity ?? 0}`
                    : ''
                }
                getOptionDisabled={(option) => Number(option.stock_quantity || 0) <= 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('farrierMaterials.name')}
                    margin="dense"
                    fullWidth
                  />
                )}
                fullWidth
              />
              <TextField
                label={t('farrierMaterials.quantity')}
                name="quantity"
                value={medication.quantity}
                onChange={(e) =>
                  handleMedicationChange(index, 'quantity', e.target.value)
                }
                fullWidth
                margin="dense"
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={() => removeMedication(index)}
              >
                {t('delete')}
              </Button>
            </Box>
          ))}
          <Button variant="contained" color="primary" onClick={addMedication}>
            {t('farrierMaterials.addNew')}
          </Button>
        </FormControl>

        <TextField
          label="Notes"
          name="notes"
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          fullWidth
          multiline
          rows={4}
          margin="normal"
        />

        <Button type="submit" variant="contained" color="primary" fullWidth>
          Update Record
        </Button>
      </form>
    </Box>
  );
};

export default EditFarrierRecord;
